/**
 * Medidor de nivel de la grabación: las barras que se mueven con tu voz.
 *
 * POR QUÉ EXISTE: el punto rojo y el cronómetro se mueven exactamente igual con el micrófono mudo,
 * con el micrófono tomado por otra aplicación o con la ganancia en cero. No prueban nada. Lo único
 * que prueba que te estamos escuchando es que esto reaccione a lo que decís.
 *
 * NO usa estado de React: lee `meterRef` (que el hook actualiza frame a frame) en su propio
 * `requestAnimationFrame` y escribe la altura directo en el DOM. Con `setState` a 60 fps, la barra
 * entera —textarea incluido— se re-renderizaría sesenta veces por segundo mientras alguien habla.
 *
 * Las barras son las BANDAS REALES del espectro, no un adorno con `Math.random()`: por eso se mueve
 * distinto con una voz grave que con una aguda, y por eso se queda quieto de verdad en silencio.
 */
import React, {useEffect, useRef} from 'react';

import {METER_BANDS, meterHeight} from '../../utils/audioRecording';

export function AudioLevelMeter({meterRef, bands = METER_BANDS, label, active = true}) {
    const barsRef = useRef([]);
    const rafRef = useRef(null);

    useEffect(() => {
        if (!active || !meterRef || typeof requestAnimationFrame !== 'function') return undefined;
        const paint = () => {
            const meter = meterRef.current || {};
            const values = meter.bands || [];
            barsRef.current.forEach((bar, index) => {
                if (!bar) return;
                // Sin medición (Web Audio ausente) las barras quedan en su piso: un medidor quieto
                // es honesto, uno inventado no.
                const height = meter.active ? meterHeight(values[index] || 0) : 0;
                bar.style.transform = `scaleY(${Math.max(0.12, height)})`;
            });
            rafRef.current = requestAnimationFrame(paint);
        };
        rafRef.current = requestAnimationFrame(paint);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, [active, meterRef]);

    return (
        // `aria-hidden`: para un lector de pantalla, una animación de nivel es ruido puro. Lo que
        // ese usuario necesita —que está grabando— ya se anuncia en la región viva de la barra.
        <span className="aud-meter" aria-hidden="true" title={label}>
            {Array.from({length: bands}).map((_, index) => (
                <i key={index} className="aud-meter__bar"
                   ref={(el) => { barsRef.current[index] = el; }}/>
            ))}
        </span>
    );
}

export default AudioLevelMeter;
